import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { ImageIcon, ZoomInIcon } from "lucide-react";
import { useState } from "react";
import type { AudienceVoteContestantDTO } from "../api/client";
import { resolveContestantPhotoPreviewUrl } from "./resolve-contestant-photo-preview";

type AudienceVoteContestantArtworkCellProps = {
  eventId: string;
  contestant: Pick<AudienceVoteContestantDTO, "displayName" | "photoUrl">;
};

export function AudienceVoteContestantArtworkCell({
  eventId,
  contestant,
}: AudienceVoteContestantArtworkCellProps) {
  const [expanded, setExpanded] = useState(false);
  const photoRef = contestant.photoUrl?.trim() ?? "";

  const previewQuery = useQuery({
    queryKey: ["audience-vote-contestant-photo", eventId, photoRef],
    queryFn: () => resolveContestantPhotoPreviewUrl(eventId, photoRef),
    enabled: photoRef.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  if (!photoRef) {
    return <span className="text-muted-foreground">—</span>;
  }

  const src = previewQuery.data;

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="group flex items-center gap-2 rounded-md border bg-background p-1 text-left transition-colors hover:bg-muted/60"
        title="ຂຢາຍເບິ່ງຜົນງານ"
      >
        <span className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/30">
          {src ? (
            <img src={src} alt="" className="size-full object-cover" />
          ) : previewQuery.isLoading ? (
            <span className="text-[10px] text-muted-foreground">…</span>
          ) : (
            <ImageIcon className="size-5 text-muted-foreground" />
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/25">
            <ZoomInIcon className="size-4 text-white opacity-0 drop-shadow group-hover:opacity-100" />
          </span>
        </span>
        <span className="hidden text-muted-foreground text-xs sm:inline">
          ຂຢາຍ
        </span>
      </button>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-h-[min(92vh,900px)] max-w-3xl gap-0 p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>{contestant.displayName}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
            {src ? (
              <img
                src={src}
                alt=""
                className="max-h-[min(75vh,800px)] w-full object-contain"
              />
            ) : previewQuery.isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
            ) : (
              <p className="text-muted-foreground text-sm">ໂຫຼດຮູບບໍ່ໄດ້</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
